import { useCallback, useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import MetadataFooter from "@/components/MetadataFooter";
import { Plus, Baby, AlertCircle, X, Heart, Pencil } from "lucide-react";

const VISIT_TYPES = [
  { value: "postpartum", label: "Postpartum support" },
  { value: "check_in", label: "Wellness check-in" },
  { value: "home_visit", label: "Home visit" },
  { value: "kit_distribution", label: "Wellness kit distribution" },
  { value: "family_followup", label: "Family follow-up" },
];

export default function MaternalWellness() {
  const { can } = useAuth();
  const [searchParams] = useSearchParams();
  const csIdParam = searchParams.get("care_seeker_id");
  const [items, setItems] = useState([]);
  const [careSeekers, setCareSeekers] = useState([]);
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = urgentOnly ? { urgent_only: true } : {};
      const [m, cs] = await Promise.all([
        api.get("/maternal", { params }),
        careSeekers.length === 0 ? api.get("/care-seekers") : Promise.resolve({ data: careSeekers }),
      ]);
      setItems(m.data);
      if (cs.data) setCareSeekers(cs.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [urgentOnly, careSeekers]);
  useEffect(() => { load(); }, [load]);

  const csName = (id) => {
    const cs = careSeekers.find((c) => c.id === id);
    return cs ? `${cs.preferred_name || cs.first_name} ${cs.last_name}` : "Care Seeker";
  };

  // Per product owner: "Maternal and family should live only under the
  // Care Seeker profile and should be editable there." This global page
  // is no longer in the sidebar; if anyone reaches it via a stale link or
  // direct URL without a care_seeker_id filter, redirect to the Care
  // Seekers list so they pick a profile first.
  if (!csIdParam) {
    return <Navigate to="/care-seekers" replace />;
  }

  return (
    <div className="space-y-6" data-testid="maternal-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Family & maternal</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Maternal & Family</h1>
          <p className="text-[#334155] mt-2">Walking alongside families during one of life's most tender seasons.</p>
        </div>
        {can("maternal.create") && (
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium" data-testid="add-maternal-btn">
            <Plus className="w-4 h-4" /> New visit
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-4 flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={urgentOnly} onChange={(e) => setUrgentOnly(e.target.checked)} data-testid="maternal-urgent-filter" />
          <span className="text-sm text-[#334155]">Urgent follow-ups only</span>
        </label>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center">
          <Baby className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No visits yet</h3>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="maternal-list">
          {items.map((m) => (
            <li key={m.id} className={`border rounded-2xl p-5 ${m.urgent_follow_up ? "bg-[#FEF3C7] border-[#FCD34D]" : "bg-white border-[#E5E5E0]"}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-display font-semibold text-[#0F172A]">{csName(m.care_seeker_id)}</h3>
                    <span className="text-[10px] bg-[#E8ECE7] text-[#1E3A8A] px-2 py-0.5 rounded-full capitalize">{m.visit_type?.replace(/_/g, " ")}</span>
                    {m.urgent_follow_up && (
                      <span className="text-[10px] bg-white text-[#92400E] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> URGENT
                      </span>
                    )}
                    {m.kit_distributed && <span className="text-[10px] bg-[#F59E0B] text-white px-2 py-0.5 rounded-full">Kit delivered</span>}
                  </div>
                  <div className="text-xs text-[#1E3A8A]">{formatDate(m.visit_date)}{m.pregnancy_status && ` · ${m.pregnancy_status}`}{m.weeks_postpartum && ` · ${m.weeks_postpartum} weeks postpartum`}</div>
                </div>
                {can("maternal.edit") && (
                  <button
                    onClick={() => setEditing(m)}
                    data-testid={`edit-maternal-${m.id}`}
                    className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] hover:bg-[#F9F8F6]"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                )}
              </div>
              {m.wellness_concerns && <div className="text-sm mt-2"><strong className="text-[#334155]">Concerns:</strong> {m.wellness_concerns}</div>}
              {m.notes && <p className="text-sm text-[#334155] mt-2 whitespace-pre-wrap">{m.notes}</p>}
              <MetadataFooter entity={m} className="mt-3 pt-2 border-t border-[#F0EFEB]" />
            </li>
          ))}
        </ul>
      )}

      {showForm && <MaternalForm careSeekers={careSeekers} onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      {editing && (
        <MaternalForm
          careSeekers={careSeekers}
          existing={editing}
          onClose={() => setEditing(null)}
          onCreated={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function MaternalForm({ careSeekers, onClose, onCreated, existing }) {
  const [form, setForm] = useState({
    care_seeker_id: existing?.care_seeker_id || "",
    visit_type: existing?.visit_type || "postpartum",
    visit_date: existing?.visit_date || new Date().toISOString().slice(0, 10),
    pregnancy_status: existing?.pregnancy_status || "postpartum",
    weeks_postpartum: existing?.weeks_postpartum ?? "",
    kit_distributed: existing?.kit_distributed ?? false,
    kit_contents: existing?.kit_contents || "",
    wellness_concerns: existing?.wellness_concerns || "",
    urgent_follow_up: existing?.urgent_follow_up ?? false,
    resource_needs: existing?.resource_needs || "",
    notes: existing?.notes || "",
    follow_up_date: existing?.follow_up_date || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!existing;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.care_seeker_id) return toast.error("Select a Care Seeker");
    setSubmitting(true);
    try {
      const payload = { ...form };
      payload.weeks_postpartum = payload.weeks_postpartum ? Number(payload.weeks_postpartum) : null;
      if (isEdit) {
        delete payload.care_seeker_id;
        await api.patch(`/maternal/${existing.id}`, payload);
        toast.success("Visit updated");
      } else {
        await api.post("/maternal", payload);
        toast.success("Visit recorded");
      }
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">{isEdit ? "Edit" : "New"} Maternal Visit</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="maternal-form">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Care Seeker</label>
            <select disabled={isEdit} value={form.care_seeker_id} onChange={(e) => setForm({ ...form, care_seeker_id: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0] disabled:bg-[#F9F8F6]" data-testid="mat-cs-select">
              <option value="">Select…</option>
              {careSeekers.map((cs) => <option key={cs.id} value={cs.id}>{cs.preferred_name || cs.first_name} {cs.last_name}</option>)}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Visit type</label>
              <select value={form.visit_type} onChange={(e) => setForm({ ...form, visit_type: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]">
                {VISIT_TYPES.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Visit date</label>
              <input type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Pregnancy status</label>
              <select value={form.pregnancy_status} onChange={(e) => setForm({ ...form, pregnancy_status: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]">
                {["pregnant", "postpartum", "trying", "other"].map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Weeks postpartum</label>
              <input type="number" value={form.weeks_postpartum} onChange={(e) => setForm({ ...form, weeks_postpartum: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]" />
            </div>
          </div>
          <label className="flex items-center gap-3 p-3 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0]">
            <input type="checkbox" checked={form.kit_distributed} onChange={(e) => setForm({ ...form, kit_distributed: e.target.checked })} />
            <span className="text-sm text-[#334155]">Wellness kit distributed</span>
          </label>
          <label className="flex items-center gap-3 p-3 rounded-xl bg-[#FEF3C7] border border-[#FCD34D]">
            <input type="checkbox" checked={form.urgent_follow_up} onChange={(e) => setForm({ ...form, urgent_follow_up: e.target.checked })} data-testid="mat-urgent" />
            <span className="text-sm text-[#92400E] font-medium">Flag urgent follow-up needed</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Wellness concerns</label>
            <textarea value={form.wellness_concerns} onChange={(e) => setForm({ ...form, wellness_concerns: e.target.value })} rows={2} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Visit notes</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]" />
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="mat-submit-btn" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Record visit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
